import { createElement, type ReactNode } from 'react'
import { RailPageShellIdentity } from './RailPageShell'

type RailPageHeaderProps = {
  /**
   * Freeform identity node. When provided, renders as-is in the leading
   * column and OVERRIDES the `badge` + `subtitle` + `title` triple below.
   * Prefer the triple for image-4-style headers; use this only when the
   * page needs a bespoke identity layout.
   */
  identity?: ReactNode
  /**
   * Optional small square chip shown before the title, matching the
   * `RailPageShellIdentity` badge (image 4). One or two characters — e.g.
   * `'L'` for Ledger, `'EH'` for Explore Hub. When either `badge` or the
   * lowercase-uppercase `subtitle` is provided AND `identity` is not, the
   * header renders an image-4 identity via `RailPageShellIdentity`.
   */
  badge?: ReactNode
  /**
   * Small uppercase label shown above the title (image 4 subtitle).
   */
  subtitle?: ReactNode
  /** @deprecated Use `subtitle` — this alias is preserved for compat. */
  eyebrow?: ReactNode
  title: ReactNode
  description?: ReactNode
  meta?: ReactNode
  center?: ReactNode
  actions?: ReactNode
  className?: string
  identityClassName?: string
  centerClassName?: string
  actionsClassName?: string
  titleClassName?: string
  descriptionClassName?: string
  titleLevel?: 'h1' | 'h2'
}

const join = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ')

export const RailPageHeader = ({
  identity,
  badge,
  subtitle,
  eyebrow,
  title,
  center,
  actions,
  className,
  identityClassName,
  centerClassName,
  actionsClassName,
  titleClassName,
  titleLevel = 'h1',
}: RailPageHeaderProps) => {
  const layoutClass = center && actions
    ? 'railPageHeader--full'
    : center
      ? 'railPageHeader--left-center'
      : actions
        ? 'railPageHeader--left-right'
        : 'railPageHeader--left-only'

  // Image-4 identity path — resolved when the caller passes `badge` or
  // `subtitle`/`eyebrow` and does not provide a freeform `identity` node.
  const resolvedSubtitle = subtitle ?? eyebrow
  const structuredIdentity = identity ?? (badge !== undefined || resolvedSubtitle !== undefined
    ? (
      <RailPageShellIdentity
        badge={badge}
        title={title}
        subtitle={resolvedSubtitle}
      />
    )
    : null)

  return (
    <header className={join('railPageHeader', layoutClass, className)}>
      <div className={join('railPageHeaderColumn', 'railPageHeaderColumn--identity', identityClassName)}>
        {structuredIdentity ?? (
          createElement(titleLevel, { className: join('railPageHeaderTitle', titleClassName) }, title)
        )}
      </div>

      {center ? (
        <div className={join('railPageHeaderColumn', 'railPageHeaderColumn--center', centerClassName)}>
          {center}
        </div>
      ) : null}

      {actions ? (
        <div className={join('railPageHeaderColumn', 'railPageHeaderColumn--actions', actionsClassName)}>
          {actions}
        </div>
      ) : null}
    </header>
  )
}
